/** @format */

import { useState, useRef } from "react";
import { useBrand } from "../hooks/useBrand";
import { uploadPdf } from "../api/papers";

export type PdfSlotKey = "paper" | "markScheme" | "specification";

export type ImportPdfKeys = Record<PdfSlotKey, string>;

interface PdfImportProps {
	onKeysChange: (keys: ImportPdfKeys | null) => void;
}

const PDF_SLOTS: { key: PdfSlotKey; label: string }[] = [
	{ key: "paper", label: "Exam paper - English" },
	{ key: "markScheme", label: "Mark scheme - English" },
	{ key: "specification", label: "Subject specification" },
];

const MAX_PDF_BYTES = 10 * 1024 * 1024;

type SlotState = {
	file?: File;
	assetKey?: string;
	error?: string;
	status: "idle" | "uploading" | "success" | "error";
};

const validatePdf = (file: File): string | undefined => {
	if (file.type !== "application/pdf") {
		return `Only PDF type is allowed (selected: ${file.type || "unknown type"}).`;
	}
	if (file.size > MAX_PDF_BYTES) {
		return "File must be 10 MB or smaller.";
	}
	return undefined;
};

export const PdfImport = ({ onKeysChange }: PdfImportProps) => {
	const { brand } = useBrand();
	const [slots, setSlots] = useState<Record<PdfSlotKey, SlotState>>({
		paper: { status: "idle" },
		markScheme: { status: "idle" },
		specification: { status: "idle" },
	});
	const inputRefs = useRef<Partial<Record<PdfSlotKey, HTMLInputElement | null>>>({});

	const updateSlot = (key: PdfSlotKey, patch: Partial<SlotState>) =>
		setSlots((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

	const handleSelect = (key: PdfSlotKey, file: File | undefined) => {
		if (!file) {
			return;
		}
		const error = validatePdf(file);
		setSlots((prev) => ({
			...prev,
			[key]: { status: "idle", file: error ? undefined : file, error },
		}));
	};

	const removePdf = (key: PdfSlotKey) => {
		setSlots((prev) => ({ ...prev, [key]: { status: "idle" } }));
		onKeysChange(null);
	};

	const handleUpload = async () => {
		const pending = PDF_SLOTS.filter(({ key }) => slots[key].status !== "success");
		const results = await Promise.all(
			pending.map(async ({ key }) => {
				const file = slots[key].file;
				if (!file) {
					return { key, assetKey: undefined };
				}
				updateSlot(key, { status: "uploading", error: undefined });
				try {
					const assetKey = await uploadPdf(brand, file);
					updateSlot(key, { status: "success", assetKey });
					return { key, assetKey };
				} catch {
					updateSlot(key, { status: "error", error: "Upload failed. Try again." });
					return { key, assetKey: undefined };
				}
			}),
		);

		const keys: Partial<ImportPdfKeys> = {};
		PDF_SLOTS.forEach(({ key }) => {
			keys[key] = slots[key].assetKey;
		});
		results.forEach(({ key, assetKey }) => {
			if (assetKey) {
				keys[key] = assetKey;
			}
		});
		if (PDF_SLOTS.every(({ key }) => keys[key])) {
			onKeysChange(keys as ImportPdfKeys);
		}
	};

	const isAllSelected = PDF_SLOTS.every(({ key }) => slots[key].file);
	const isUploading = PDF_SLOTS.some(({ key }) => slots[key].status === "uploading");
	const isAllUploaded = PDF_SLOTS.every(({ key }) => slots[key].status === "success");

	return (
		<div>
			<p className="mt-1 mb-4 text-sm text-slate-600">
				Select the exam paper, mark scheme and subject specification. Each file must be a PDF of 10 MB or smaller.
			</p>

			<div className="flex flex-row gap-8">
				{PDF_SLOTS.map(({ key, label }) => {
					const slot = slots[key];
					return (
						<div key={key} className="flex flex-col gap-2">
							<span className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">{label}</span>
							{slot.file ? (
								<div className="flex items-center justify-between gap-6 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
									<span className="w-48 truncate text-sm font-medium text-slate-700">{slot.file.name}</span>
									<button
										type="button"
										onClick={() => removePdf(key)}
										disabled={isUploading}
										className="text-sm font-semibold text-red-600 hover:text-red-700 disabled:opacity-60 cursor-pointer shrink-0"
									>
										Remove
									</button>
								</div>
							) : (
								<div className="flex items-center gap-3">
									<input
										ref={(element) => {
											inputRefs.current[key] = element;
										}}
										type="file"
										accept="application/pdf"
										aria-label={label}
										className="hidden"
										onChange={(event) => {
											handleSelect(key, event.target.files?.[0]);
											event.target.value = "";
										}}
									/>
									<button
										type="button"
										onClick={() => inputRefs.current[key]?.click()}
										disabled={isUploading}
										aria-label={`Choose ${label}`}
										className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60 cursor-pointer"
									>
										Choose PDF
									</button>
								</div>
							)}
							{slot.error && <span className="text-sm text-red-600">{slot.error}</span>}
							{slot.status === "uploading" && <span className="text-sm text-slate-600">Uploading…</span>}
							{slot.status === "success" && <span className="text-sm text-green-700">Uploaded</span>}
						</div>
					);
				})}
			</div>

			<button
				type="button"
				onClick={() => void handleUpload()}
				disabled={!isAllSelected || isUploading || isAllUploaded}
				className="mt-6 rounded-md bg-(--admin-color-primary-strong) px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 cursor-pointer disabled:cursor-not-allowed"
			>
				{isUploading ? "Uploading…" : "Upload all"}
			</button>
		</div>
	);
};
